import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {createSelector, wrapInShallowSelector} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {getColorHighlight} from '../../../../../helpers/models/highlightHelpers';
import {findParentTags} from '../../../../../helpers/models/tagHelpers';
import {TagModel} from '../../../../../models/api/tagModel';
import {getTags} from '../../../../../store/data/tags/tagsSelectors';
import {getUser} from '../../../../../store/data/teammates/teammatesSelectors';
import {ActivityPill} from '../activityTagPill/activityPill';
import {ActivityTagPillContent} from '../activityTagPill/activityTagPillContent';
import {ActivityTagPillTooltip} from '../activityTagPill/activityTagPillTooltip';

/*
 * Props.
 */

interface CommentBodyAiTagSuggestionTagPillProps {
  tag: TagModel;
  className?: string;
}

/*
 * Style.
 */

const StyledContainerDiv = styled.div`
  display: inline-block;

  &:not(:first-of-type) {
    margin-left: 3px;
  }
`;

/*
 * Component.
 */

export const CommentBodyAiTagSuggestionTagPill: FC<CommentBodyAiTagSuggestionTagPillProps> = (props) => {
  const {tag, className} = props;
  const tagsPath = useTagsPath(tag);
  const color = getColorHighlight(tag);
  const user = useAppSelector(getUser);
  const isPrivate = tag.namespace === user.namespace;

  return (
    <StyledContainerDiv className={className}>
      <ActivityPill
        size={VisualSizesEnum.MEDIUM}
        hasHover={false}
        hasHoverStyle={false}
        color={color}
        isPrivate={isPrivate}
        shouldUppercase
        renderTooltip={() => <ActivityTagPillTooltip tagsPath={tagsPath} />}
      >
        <ActivityTagPillContent tagsPath={tagsPath} />
      </ActivityPill>
    </StyledContainerDiv>
  );
};

function useTagsPath(tag: TagModel) {
  const getTagsPath = useMemo(
    () => wrapInShallowSelector(createSelector([getTags], (tags) => [tag, ...findParentTags(tags, tag)])),
    [tag],
  );
  return useAppSelector(getTagsPath);
}
